<script setup>
import { onBeforeUnmount, onMounted, ref } from "vue";

// A row of tiles: one line that scrolls sideways on phones, wrapping on computers. On
// phones a fade and an arrow show at whichever end has more to see.
const row = ref(null);
const more = ref({ left: false, right: false });

const update = () => {
  const el = row.value;
  if (!el) return;
  more.value = {
    left: el.scrollLeft > 4,
    right: el.scrollLeft + el.clientWidth < el.scrollWidth - 4,
  };
};

// Tiles arrive (and the screen turns) after the row is drawn, so look again then
let resized;
let changed;
onMounted(() => {
  update();
  resized = new ResizeObserver(update);
  resized.observe(row.value);
  changed = new MutationObserver(update);
  changed.observe(row.value, { childList: true, subtree: true });
});
onBeforeUnmount(() => {
  resized?.disconnect();
  changed?.disconnect();
});

const scroll = (direction) =>
  row.value?.scrollBy({
    left: direction * row.value.clientWidth * 0.7,
    behavior: "smooth",
  });
</script>

<template>
  <div class="relative">
    <div
      ref="row"
      class="tabs -mx-4 -my-1 flex overflow-x-auto px-4 py-1 md:-mx-16 md:px-16 [&>*]:shrink-0 lg:mx-0 lg:my-0 lg:flex-wrap lg:gap-y-4 lg:overflow-visible lg:p-0"
      @scroll.passive="update"
    >
      <slot />
    </div>
    <template v-for="side in ['left', 'right']" :key="side">
      <Transition name="hint">
        <div
          v-if="more[side]"
          class="pointer-events-none absolute inset-y-0 flex w-20 items-center lg:hidden"
          :class="
            side === 'right'
              ? '-right-4 justify-end bg-gradient-to-l from-white via-white/80 to-transparent pr-3 md:-right-16 md:pr-14'
              : '-left-4 justify-start bg-gradient-to-r from-white via-white/80 to-transparent pl-3 md:-left-16 md:pl-14'
          "
          aria-hidden="true"
        >
          <button
            type="button"
            tabindex="-1"
            class="pointer-events-auto flex h-9 w-9 items-center justify-center rounded-full bg-white text-primary shadow-md ring-1 ring-gray-200"
            :class="{ nudge: side === 'right' }"
            @click="scroll(side === 'right' ? 1 : -1)"
          >
            <font-awesome-icon
              :icon="[
                'fas',
                side === 'right' ? 'chevron-right' : 'chevron-left',
              ]"
              class="text-sm"
            />
          </button>
        </div>
      </Transition>
    </template>
  </div>
</template>

<style scoped>
.hint-enter-active,
.hint-leave-active {
  transition: opacity 0.2s ease;
}
.hint-enter-from,
.hint-leave-to {
  opacity: 0;
}

/* The arrow nudges a few times to say the row goes on */
.nudge {
  animation: nudge 1.2s ease-in-out 0.6s 3;
}
@keyframes nudge {
  0%,
  100% {
    transform: translateX(0);
  }
  50% {
    transform: translateX(4px);
  }
}
@media (prefers-reduced-motion: reduce) {
  .nudge {
    animation: none;
  }
}
</style>
